/**
 * PlayerSprite - Pixel art sprite for the player character with directional facing
 */

// Color palette for player sprite
const C = {
  // Transparent
  _: null,
  // Skin tones
  SK: '#f0c8a0', // skin
  SD: '#d4a878', // skin shadow
  // Hair
  HR: '#4a3728', // brown hair
  HD: '#2a1f18', // hair dark
  // Armor/clothes (teal theme matching player color)
  AL: '#4ecca3', // armor light (main player color)
  AD: '#3a9a7a', // armor dark
  AM: '#45b892', // armor medium
  // Cape/cloth
  CL: '#2a6a5a', // cape light
  CD: '#1a4a3a', // cape dark
  // Metal/accents
  MT: '#c0c0c0', // metal silver
  MD: '#808080', // metal dark
  GL: '#f1c40f', // gold accent
  // Belt/leather
  BL: '#5a4030', // leather brown
  BD: '#3a2820', // leather dark
  // Eyes
  EY: '#2a4a6a', // blue eyes
  // Common
  BK: '#000000', // black
  WT: '#ffffff', // white
};

type Color = string | null;

// 12x12 pixel sprite for the player (same size as enemies)
const SPRITE_SIZE = 12;

// Direction type
export type FacingDirection = 'down' | 'up' | 'left' | 'right';

// ============================================
// FACING DOWN (towards camera) - default view
// ============================================
const heroDown: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._],
  [C._,C._,C.SD,C.SK,C.EY,C.SK,C.EY,C.SK,C.SD,C._,C._,C._],
  [C._,C._,C._,C.SD,C.SK,C.SK,C.SK,C.SD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C.SK,C.SD,C._,C._,C._,C._,C._],
  [C._,C.CD,C.AD,C.AL,C.AL,C.GL,C.AL,C.AL,C.AD,C.CD,C._,C._],
  [C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AL,C.AL,C.AD,C.CL,C.CD,C._],
  [C._,C.CD,C.SK,C.AD,C.AL,C.AL,C.AL,C.AD,C.SK,C.CD,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.BD,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

const heroDownAttack: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._],
  [C._,C._,C.SD,C.SK,C.EY,C.SK,C.EY,C.SK,C.SD,C._,C._,C._],
  [C._,C._,C._,C.SD,C.SK,C.SK,C.SK,C.SD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C.SK,C.SD,C._,C._,C.SK,C._,C._],
  [C._,C.CD,C.AD,C.AL,C.AL,C.GL,C.AL,C.AL,C.AD,C.SK,C._,C._],
  [C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AL,C.AL,C.AD,C.SD,C._,C._],
  [C._,C.CD,C.SK,C.AD,C.AL,C.AL,C.AL,C.AD,C.CD,C._,C._,C._],
  [C._,C._,C.SD,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.BD,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

// ============================================
// FACING UP (away from camera) - back view
// ============================================
const heroUp: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.HD,C.SD,C.HD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.SD,C._,C._,C._,C._,C._,C._],
  [C._,C.CL,C.CD,C.AD,C.AL,C.AL,C.AL,C.AD,C.CD,C.CL,C._,C._],
  [C.CL,C.CL,C.CL,C.CD,C.AL,C.AL,C.AL,C.CD,C.CL,C.CL,C.CL,C._],
  [C._,C.CL,C.SK,C.CD,C.AD,C.AD,C.AD,C.CD,C.SK,C.CL,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.BD,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

const heroUpAttack: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.HD,C.SD,C.HD,C._,C._,C.SK,C._,C._],
  [C._,C._,C._,C._,C._,C.SD,C._,C._,C._,C.SK,C._,C._],
  [C._,C.CL,C.CD,C.AD,C.AL,C.AL,C.AL,C.AD,C.CD,C.SD,C._,C._],
  [C.CL,C.CL,C.CL,C.CD,C.AL,C.AL,C.AL,C.CD,C.CL,C.CL,C._,C._],
  [C._,C.CL,C.SK,C.CD,C.AD,C.AD,C.AD,C.CD,C.CL,C._,C._,C._],
  [C._,C._,C.SD,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.BD,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

// ============================================
// FACING LEFT - side view
// ============================================
const heroLeft: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C._,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._,C._],
  [C._,C._,C.SD,C.EY,C.SK,C.SK,C.SK,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C.SK,C.SK,C.SK,C.SD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SK,C.SD,C._,C._,C._,C._,C._,C._],
  [C._,C._,C.SK,C.AD,C.AL,C.GL,C.AL,C.AD,C.CD,C._,C._,C._],
  [C._,C.SD,C.AD,C.AL,C.AL,C.AL,C.AL,C.AD,C.CL,C.CD,C._,C._],
  [C._,C._,C._,C.AD,C.AL,C.AL,C.AL,C.AD,C.CL,C.CD,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

const heroLeftAttack: Color[][] = [
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C._,C._,C._,C._,C._],
  [C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._,C._],
  [C._,C._,C.SD,C.EY,C.SK,C.SK,C.SK,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C.SK,C.SK,C.SK,C.SD,C._,C._,C._,C._,C._],
  [C.SK,C._,C._,C._,C.SK,C.SD,C._,C._,C._,C._,C._,C._],
  [C.SK,C._,C.SD,C.AD,C.AL,C.GL,C.AL,C.AD,C.CD,C._,C._,C._],
  [C.SD,C._,C.AD,C.AL,C.AL,C.AL,C.AL,C.AD,C.CL,C.CD,C._,C._],
  [C._,C._,C._,C.AD,C.AL,C.AL,C.AL,C.AD,C.CL,C.CD,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.BD,C._,C.BD,C.BD,C._,C._,C._,C._],
];

// ============================================
// FACING RIGHT - side view (mirrored)
// ============================================
const heroRight: Color[][] = [
  [C._,C._,C._,C._,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.SK,C.SK,C.SK,C.EY,C.SD,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C.SK,C.SK,C.SK,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.SD,C.SK,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.CD,C.AD,C.AL,C.GL,C.AL,C.AD,C.SK,C._,C._],
  [C._,C._,C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AL,C.AD,C.SD,C._],
  [C._,C._,C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AD,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BD,C._,C.BD,C._,C._,C._,C._],
];

const heroRightAttack: Color[][] = [
  [C._,C._,C._,C._,C.HR,C.HR,C.HR,C.HD,C._,C._,C._,C._],
  [C._,C._,C._,C.HD,C.HR,C.HR,C.HR,C.HR,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.SK,C.SK,C.SK,C.SK,C.HD,C._,C._,C._],
  [C._,C._,C._,C.HD,C.SK,C.SK,C.SK,C.EY,C.SD,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C.SK,C.SK,C.SK,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.SD,C.SK,C._,C._,C._,C.SK,C._],
  [C._,C._,C._,C.CD,C.AD,C.AL,C.GL,C.AL,C.AD,C.SD,C.SK,C._],
  [C._,C._,C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AL,C.AD,C.SD,C._],
  [C._,C._,C.CD,C.CL,C.AD,C.AL,C.AL,C.AL,C.AD,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.BD,C.BL,C.BD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.AD,C._,C.AD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.BD,C.BD,C._,C.BD,C._,C._,C._,C._],
];

// Sprite map by direction
const spriteMap: { [key in FacingDirection]: { idle: Color[][]; attack: Color[][] } } = {
  down: { idle: heroDown, attack: heroDownAttack },
  up: { idle: heroUp, attack: heroUpAttack },
  left: { idle: heroLeft, attack: heroLeftAttack },
  right: { idle: heroRight, attack: heroRightAttack },
};

/**
 * Convert a facing Vector2 to a FacingDirection
 */
export function vectorToDirection(facingX: number, facingY: number): FacingDirection {
  // In isometric view, we need to consider the angle
  // facingX and facingY are normalized direction vectors

  const absX = Math.abs(facingX);
  const absY = Math.abs(facingY);

  // Determine primary direction
  if (absX > absY) {
    // Horizontal is dominant
    return facingX > 0 ? 'right' : 'left';
  } else {
    // Vertical is dominant
    // In isometric: positive Y is "down-right", negative Y is "up-left"
    // But for sprite facing, we simplify:
    return facingY > 0 ? 'down' : 'up';
  }
}

/**
 * Render the player sprite at the given screen position
 * @param ctx Canvas 2D context
 * @param screenX Screen X position (center)
 * @param screenY Screen Y position (bottom)
 * @param size The size to render (width and height)
 * @param facingX X component of facing direction
 * @param facingY Y component of facing direction
 * @param isAttacking Whether player is attacking
 * @param flashDamage Whether to flash white (damage taken)
 */
export function renderPlayerSprite(
  ctx: CanvasRenderingContext2D,
  screenX: number,
  screenY: number,
  size: number,
  facingX: number,
  facingY: number,
  isAttacking: boolean = false,
  flashDamage: boolean = false
): void {
  // Determine direction from facing vector
  const direction = vectorToDirection(facingX, facingY);
  const sprites = spriteMap[direction];
  const sprite = isAttacking ? sprites.attack : sprites.idle;

  const pixelSize = size / SPRITE_SIZE;
  const startX = screenX - size / 2;
  const startY = screenY - size;

  for (let row = 0; row < SPRITE_SIZE; row++) {
    for (let col = 0; col < SPRITE_SIZE; col++) {
      const color = sprite[row]?.[col];
      if (color) {
        if (flashDamage) {
          // Flash white when taking damage
          ctx.fillStyle = blendWithWhite(color, 0.6);
        } else if (isAttacking) {
          // Slight brightness boost when attacking
          ctx.fillStyle = brighten(color);
        } else {
          ctx.fillStyle = color;
        }
        ctx.fillRect(
          startX + col * pixelSize,
          startY + row * pixelSize,
          pixelSize + 0.5, // Small overlap to prevent gaps
          pixelSize + 0.5
        );
      }
    }
  }
}

/**
 * Brighten a color slightly for attack animation
 */
function brighten(color: string): string {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  const newR = Math.min(255, r + 30);
  const newG = Math.min(255, g + 30);
  const newB = Math.min(255, b + 30);

  return `rgb(${newR}, ${newG}, ${newB})`;
}

/**
 * Blend a color with white for damage flash effect
 */
function blendWithWhite(color: string, amount: number): string {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  return `rgb(${newR}, ${newG}, ${newB})`;
}
